import { Backdrop, CircularProgress, Dialog, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import React, { Dispatch, useEffect, useState } from 'react';
import { Container } from './InternalUserList.styled';
import PageTitle from '../../components/page-title/PageTitle';
import { IoCloseCircleOutline } from 'react-icons/io5';
import IInternalUser from '../../interfaces/IInternalUser';
import IInternalUserAccessScope from '../../interfaces/IInternalUserAccessScope';
import { useLocation } from 'react-router-dom';
import { useInternalUserService } from '../../hooks/services/useInternalUserService';
import { useInternalUserAccessScopeService } from '../../hooks/services/useInternalUserAccessScopeService';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import { Form } from './InternalUserForm.styled';
import IFormError from '../../interfaces/IFormError';
import * as yup from 'yup';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';
import { LoadingButton } from '@mui/lab';

interface IProps {
  isOpen: boolean;
  id: string;
  action: 'new' | 'edit';
  setIsOpen: Dispatch<React.SetStateAction<boolean>>;
  updateScopeList(): void;
}

const initialScope: IInternalUserAccessScope = {
  companyCode: '',
  subsidiaryCode: '',
  userId: '',
};

const InternalUserAccessScopeForm = (props: IProps) => {
  const [user, setUser] = useState<IInternalUser>();
  const [scope, setScope] = useState<IInternalUserAccessScope>({ ...initialScope });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<IFormError | null>(null);

  const location = useLocation();
  const userService = useInternalUserService();
  const userAccessScopeService = useInternalUserAccessScopeService();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();

  const listUser = async (userIdParam: string) => {
    setLoading(true);
    await userService
      .listById(userIdParam)
      .then(result => {
        setUser(result);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const listScope = async () => {
    setLoading(true);
    await userAccessScopeService
      .listById(props.id)
      .then(result => {
        setScope({ ...result });
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const userId = location.pathname.split('/')[location.pathname.split('/').length - 1];
    listUser(userId);

    if (props.action === 'edit') {
      listScope();
    }
  }, [props.isOpen]);

  const schema = yup.object({
    companyCode: yup.string().required('Campo obrigatório'),
    subsidiaryCode: yup.string().required('Campo obrigatório'),
  });

  const handleClose = () => {
    setScope({ ...initialScope });
    props.setIsOpen(false);
    props.updateScopeList();
  };

  const create = async (): Promise<void> => {
    setLoading(true);
    await userAccessScopeService
      .create({
        companyCode: scope.companyCode,
        subsidiaryCode: scope.subsidiaryCode,
        userId: `${user?.id}`,
      })
      .then(result => {
        setLoading(false);
        toastr.success('Abrangência cadastrada com sucesso!');
      })
      .catch(error => {
        setLoading(false);
      });
  };

  const update = async (): Promise<void> => {
    setLoading(true);
    await userAccessScopeService
      .update(props.id, {
        id: props.id,
        companyCode: scope.companyCode,
        subsidiaryCode: scope.subsidiaryCode,
        userId: `${user?.id}`,
      })
      .then(result => {
        setLoading(false);
        toastr.success('Abrangência atualizada com sucesso!');
      })
      .catch(error => {
        setLoading(false);
      });
  };

  const handleSubmit = async () => {
    const result = await validation.validate(schema, scope);

    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }
    if (props.action === 'edit') {
      await update();
    } else {
      await create();
    }
    handleClose();
  };

  return (
    <Dialog fullWidth open={props.isOpen} maxWidth="md">
      <Container>
        <PageTitle
          title={`${
            props.action === 'edit' ? 'EDITANDO' : 'CRIANDO'
          } ABRANGENCIA DO USUÁRIO ${user?.fullName?.toUpperCase()}`}
        >
          <Tooltip title="Fechar" placement="left-start">
            <IconButton onClick={handleClose}>
              <IoCloseCircleOutline />
            </IconButton>
          </Tooltip>
        </PageTitle>
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código da Empresa"
                placeholder="Informe código da empresa"
                value={scope?.companyCode}
                onChange={e => setScope({ ...scope, companyCode: e.target.value })}
                error={Boolean(errors?.companyCode)}
                helperText={errors?.companyCode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código da filial"
                placeholder="Informe código da filial"
                value={scope?.subsidiaryCode}
                onChange={e => setScope({ ...scope, subsidiaryCode: e.target.value })}
                error={Boolean(errors?.subsidiaryCode)}
                helperText={errors?.subsidiaryCode}
              />
            </Grid>
          </Grid>
        </Form>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ marginTop: 5 }}
          spacing={2}
        >
          <Grid item>
            <LoadingButton loading={loading} variant="contained" color="secondary" onClick={handleClose}>
              Cancelar
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSubmit}>
              Salvar
            </LoadingButton>
          </Grid>
        </Grid>
      </Container>
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default InternalUserAccessScopeForm;
